import React from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Typography } from "@mui/material";
import { AnonAnalysisView } from "../Components/Analysis/AnonView";
import { getUserName } from "../DataTransform";
import { useQuery } from "@apollo/client";
import { ResultsResult, GET_RESULTS } from "../Queries";
import { ThinLayout } from "../Layouts/ThinLayout";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { useTranslation } from "react-i18next";

// Hotfix: surface the BE "released" gate as a friendly message rather
// than the generic error boundary. The BE message is stable; we match
// case-insensitively in case it gets reworded.
const isUnreleasedError = (error: unknown): boolean => {
  const message =
    (error as { message?: string } | undefined)?.message?.toLowerCase() ?? "";
  return message.includes("not been released");
};

export const ResultsAnon = () => {
  const [searchParams] = useSearchParams();
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();

  const key = searchParams.get("key");
  const resultsKeyBlob = atob(key || "");
  const resultsParts = resultsKeyBlob.split(":");

  const resultsId = resultsParts[0];
  const resultsToken = resultsParts[1];

  //
  // Queries
  //

  const {
    loading: resultsLoading,
    data: resultsData,
    error: resultsError,
  } = useQuery<ResultsResult>(GET_RESULTS, {
    variables: { id: resultsId },
    context: {
      headers: {
        Authorization: `JWT ${resultsToken}`,
      },
    },
    onError: (error) => {
      // Defense-in-depth for the released-flag retraction incident:
      // don't surface the generic "errors.results.list" boundary if the
      // gate fired — the UI below will render a specific message.
      if (isUnreleasedError(error)) return;
      setError(t("errors.results.list"), error);
    },
  });

  const unreleased = !!resultsError && isUnreleasedError(resultsError);

  const results = resultsData ? resultsData.results : undefined;

  const showLoading = resultsLoading;

  const campaign = results?.campaign;
  const user = results?.user;
  const analyses = results?.analyses;
  const aggregates = results?.aggregates;
  const overall = results?.overall;
  const template = results?.form;
  const hasMultipleRaterTypes = results?.hasMultipleRaterTypes ?? false;
  const hideOverallAverages = campaign?.course?.hideOverallAverages ?? false;

  const title = `${user ? getUserName(user) : "Your"} results for ${
    campaign?.name
  }`;

  if (unreleased) {
    return (
      <ThinLayout title="Results unavailable" loading={false}>
        <Box sx={{ maxWidth: 640, mx: "auto", mt: 4 }}>
          <Alert severity="info">
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Results are not currently available.
            </Typography>
            <Typography variant="body2">
              The instructor has not released results for this survey yet, or
              has retracted a previous release. Please check back later or
              contact your instructor with any questions.
            </Typography>
          </Alert>
        </Box>
      </ThinLayout>
    );
  }

  return (
    <ThinLayout title={title} loading={showLoading}>
      {template && campaign && user && analyses && aggregates && overall && (
        <AnonAnalysisView
          title={title}
          campaign={campaign}
          user={user}
          analyses={analyses}
          aggregates={aggregates}
          overall={overall}
          template={template}
          resultsToken={resultsToken}
          hasMultipleRaterTypes={hasMultipleRaterTypes}
          hideOverallAverages={hideOverallAverages}
        />
      )}
    </ThinLayout>
  );
};
